import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { documentsAPI } from '../services/api';
import LoadingSpinner from './LoadingSpinner';
import ErrorAlert from './ErrorAlert';

const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const getDocumentTypeLabel = (type) => {
  const labels = {
    bol: 'Bill of Lading',
    invoice: 'Invoice',
    packing_list: 'Packing List',
    photo: 'Photo',
    other: 'Other'
  };
  return labels[type] || type;
};

const DocumentsTab = ({ requestId }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [uploadSuccess, setUploadSuccess] = useState(null);
  const queryClient = useQueryClient();

  const { data: documentsData, isLoading, error, refetch } = useQuery(
    ['documents', requestId],
    () => documentsAPI.getDocuments(requestId),
    {
      enabled: !!requestId,
      staleTime: 30000
    }
  );

  const uploadMutation = useMutation(
    ({ file, documentType, description }) =>
      documentsAPI.uploadDocument(requestId, file, documentType, description),
    {
      onSuccess: () => {
        setUploadSuccess('Document uploaded successfully!');
        setUploadError(null);
        refetch();
        const fileInput = document.getElementById(`file-upload-${requestId}`);
        if (fileInput) fileInput.value = '';
      },
      onError: (err) => {
        setUploadError(err.response?.data?.message || 'Failed to upload document');
        setUploadSuccess(null);
      },
      onSettled: () => {
        setUploading(false);
      }
    }
  );

  const deleteMutation = useMutation(
    (documentId) => documentsAPI.deleteDocument(requestId, documentId),
    {
      onSuccess: () => refetch(),
      onError: (err) => console.error('Delete error:', err)
    }
  );

  const handleFileUpload = (event) => {
    event.preventDefault();
    const formData = new FormData(event.target);
    const file = formData.get('file');
    const documentType = formData.get('documentType');
    const description = formData.get('description');

    if (!file) {
      setUploadError('Please select a file to upload');
      return;
    }

    setUploading(true);
    setUploadError(null);
    setUploadSuccess(null);

    uploadMutation.mutate({ file, documentType, description });
  };

  const handleDelete = (documentId) => {
    if (window.confirm('Are you sure you want to delete this document?')) {
      deleteMutation.mutate(documentId);
    }
  };

  if (isLoading) return <LoadingSpinner />;
  if (error) return <ErrorAlert message="Failed to load documents" />;

  const documents = documentsData?.data || [];

  return (
    <div className="bg-white shadow overflow-hidden sm:rounded-lg">
      <div className="px-4 py-5 sm:px-6">
        <h3 className="text-lg leading-6 font-medium text-gray-900">Documents</h3>
        <p className="mt-1 max-w-2xl text-sm text-gray-500">
          Upload and manage documents for this request (BOLs, packing lists, photos, etc.)
        </p>
      </div>

      <div className="border-t border-gray-200 px-4 py-5 sm:p-6">
        <form onSubmit={handleFileUpload} className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <label htmlFor={`file-upload-${requestId}`} className="block text-sm font-medium text-gray-700">File</label>
              <input
                id={`file-upload-${requestId}`}
                name="file"
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.gif,.doc,.docx,.xls,.xlsx,.txt,.csv"
                className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
                required
              />
            </div>
            <div>
              <label htmlFor={`doc-type-${requestId}`} className="block text-sm font-medium text-gray-700">Document Type</label>
              <select
                id={`doc-type-${requestId}`}
                name="documentType"
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
              >
                <option value="other">Other</option>
                <option value="bol">Bill of Lading</option>
                <option value="invoice">Invoice</option>
                <option value="packing_list">Packing List</option>
                <option value="photo">Photo</option>
              </select>
            </div>
            <div>
              <label htmlFor={`doc-desc-${requestId}`} className="block text-sm font-medium text-gray-700">Description (Optional)</label>
              <input
                id={`doc-desc-${requestId}`}
                name="description"
                type="text"
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                placeholder="Brief description..."
              />
            </div>
          </div>
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={uploading}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
            >
              {uploading ? 'Uploading...' : 'Upload Document'}
            </button>
          </div>
        </form>

        {uploadError && (
          <div className="mt-4 bg-red-50 border-l-4 border-red-400 p-4">
            <div className="text-sm text-red-700">{uploadError}</div>
          </div>
        )}
        {uploadSuccess && (
          <div className="mt-4 bg-green-50 border-l-4 border-green-400 p-4">
            <div className="text-sm text-green-700">{uploadSuccess}</div>
          </div>
        )}
      </div>

      <div className="border-t border-gray-200">
        {documents.length === 0 ? (
          <div className="px-4 py-12 text-center">
            <div className="text-sm text-gray-500">No documents uploaded yet.</div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">File Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Size</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Uploaded</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {documents.map((doc) => (
                  <tr key={doc.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {doc.original_name}
                      {doc.description && (
                        <div className="text-xs text-gray-500 mt-1">{doc.description}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        {getDocumentTypeLabel(doc.document_type)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatFileSize(doc.file_size)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {doc.created_at ? new Date(doc.created_at).toLocaleDateString() :
                       doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString() :
                       'Unknown'}
                      <div className="text-xs text-gray-400">
                        by {doc.UploadedBy?.name || 'Unknown'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                      <button
                        onClick={async () => {
                          try {
                            const urlResponse = await documentsAPI.getDocumentUrl(requestId, doc.id);
                            window.open(urlResponse.url, '_blank', 'noopener,noreferrer');
                          } catch (err) {
                            alert('Failed to open document. Please try again.');
                          }
                        }}
                        className="text-indigo-600 hover:text-indigo-900"
                      >
                        View
                      </button>
                      <button
                        onClick={() => handleDelete(doc.id)}
                        className="text-red-600 hover:text-red-900"
                        disabled={deleteMutation.isLoading}
                        aria-label={`Delete ${doc.original_name}`}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentsTab;
